import { formatCurrencyAmount } from '@modules/Orders/shared/hooks/useCurrency';
import { hasAccountingEntryPayload } from '@modules/Wallets/AccountingSync/lib/accountingEntryDisplay';
import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { usePermissions } from '@shared/hooks/usePermissions';
import { faFileInvoice } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ColumnDef } from '@tanstack/react-table';
import type { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { cn } from '@shared/utils/utils';
import { getTransactionDescription } from '../lib/walletTransactionDescription';
import type { WalletTransaction } from '../models/Wallet';

const DEFAULT_EMPTY_VALUE = 'N/D';

const formatCurrency = (value: number, currency?: string, emptyValueLabel: string = DEFAULT_EMPTY_VALUE) => {
  if (Number.isNaN(value)) return emptyValueLabel;
  return formatCurrencyAmount(value, currency || 'EUR');
};

const convertToTanStackColumns = (agGridColumns: ColDef[]): ColumnDef<WalletTransaction>[] =>
  agGridColumns
    .filter((col) => {
      const field = col.field || col.headerName || 'unknown';
      return field !== 'actions' && field !== 'unknown';
    })
    .map((col) => ({
      id: col.field || (col.headerName as string) || 'unknown',
      accessorKey: col.field || (col.headerName as string) || 'unknown',
      header: col.headerName || 'Columna',
    }));

const filterColumnsBySelection = (columns: ColDef[], selectedColumns: string[]) => {
  if (!selectedColumns.length) {
    return columns;
  }

  const alwaysVisible = new Set<string>(['actions']);
  const selectedSet = new Set(selectedColumns);

  return columns.filter((col) => {
    const field = (col.field || col.headerName || 'unknown') as string;
    if (alwaysVisible.has(field)) return true;
    return selectedSet.has(field);
  });
};

export interface UseWalletTransactionsColumnsOptions {
  currency?: string;
  selectedColumns?: string[];
  /** Columna de asiento contable / conciliación (solo tenant 3PL). */
  isTenant3pl?: boolean;
  onOpenAccountingEntry?: (transaction: WalletTransaction) => void;
}

export const useWalletTransactionsColumns = ({
  currency,
  selectedColumns = [],
  isTenant3pl = false,
  onOpenAccountingEntry,
}: UseWalletTransactionsColumnsOptions = {}) => {
  const { t } = useTranslation('walletsModule');
  const { getTeamType } = usePermissions();
  const emptyValueLabel = t('table.emptyValue');
  const isTenantExternal = getTeamType() === 'tenant_external';

  const allColumnDefs: ColDef[] = useMemo(() => {
    const baseColumns: CustomColDef[] = [
      // Columna ID - Solo visible para usuarios NO tenant_external
      ...(!isTenantExternal
        ? [
            {
              field: 'id',
              headerName: t('table.columns.id'),
              width: 120,
              sortable: false,
              cellRenderer: (params: any) => <span className="text-sm">#{params.data.id}</span>,
            } as CustomColDef,
          ]
        : []),
      {
        field: 'type',
        headerName: t('table.columns.type'),
        sortable: false,
        cellRenderer: (params: any) => {
          const rawType = (params.data.type ?? 'UNKNOWN') as string;
          const normalizedType = rawType.toUpperCase();
          const label = t(`table.type.${normalizedType.toLowerCase()}`, normalizedType);
          const positiveTypes = new Set(['REFUND', 'INCREASE', 'WITHDRAWAL_RETURN', 'TOPUP']);
          const isPositiveType = positiveTypes.has(normalizedType);
          const badgeClassName = isPositiveType ? 'text-success bg-card-border' : 'text-badge-status-1 bg-card-border';

          return (
            <span className={`text-xs font-medium tracking-wide px-2 py-1 rounded-full ${badgeClassName}`}>
              {label}
            </span>
          );
        },
      },
      {
        field: 'description',
        headerName: t('table.columns.description'),
        minWidth: 400,
        sortable: false,
        cellRenderer: (params: any) => {
          const transaction = params.data as WalletTransaction;
          const { text, link } = getTransactionDescription(transaction, emptyValueLabel);

          if (link) {
            return (
              <Link to={link} className="text-sm text-primary hover:underline transition-colors">
                {text}
              </Link>
            );
          }

          return <span className="text-sm text-text">{text}</span>;
        },
      },
      {
        field: 'amount',
        headerName: t('table.columns.amount'),
        sortable: false,
        headerClass: 'justify-end',
        cellClass: 'justify-end',
        cellRenderer: (params: any) => {
          const transaction = params.data as WalletTransaction;
          const currencyCode = transaction.currency ?? currency;
          const amount = transaction.amount ?? 0;
          const formattedAmount = formatCurrency(amount, currencyCode, emptyValueLabel);
          const normalizedType = (transaction.type ?? '').toUpperCase();
          const isCollection = normalizedType === 'COLLECTION';
          const isNegative = (typeof amount === 'number' && amount < 0) || isCollection;

          return (
            <span className={`text-base font-medium ${isNegative ? 'text-error' : 'text-success'}`}>
              {formattedAmount}
            </span>
          );
        },
      },
      {
        field: 'balance_total',
        headerName: t('table.columns.balanceTotal'),
        sortable: false,
        headerClass: 'justify-end',
        cellClass: 'justify-end',
        cellRenderer: (params: any) => {
          const transaction = params.data as WalletTransaction;
          const currencyCode = transaction.currency ?? currency;
          const balanceTotal = transaction.balance_total ?? 0;
          const formattedBalance = formatCurrency(balanceTotal, currencyCode, emptyValueLabel);

          return <span className="text-sm font-medium">{formattedBalance}</span>;
        },
      },
      {
        field: 'created_at',
        headerName: t('table.columns.createdAt'),
        sortable: false,
        valueGetter: (params) => params.data.created_at,
        cellRenderer: (params: any) => {
          const createdAt = params.data.created_at;
          if (!createdAt) {
            return <span className="text-sm">{emptyValueLabel}</span>;
          }

          const date = new Date(createdAt);
          if (Number.isNaN(date.getTime())) {
            return <span className="text-xs">{emptyValueLabel}</span>;
          }

          return (
            <span className="text-sm">
              {new Intl.DateTimeFormat('es-ES', {
                dateStyle: 'short',
                timeStyle: 'short',
              }).format(date)}
            </span>
          );
        },
      },
      ...(isTenant3pl
        ? [
            {
              field: 'accounting_reconciliation',
              headerName: t('table.columns.accountingReconciliation'),
              width: 72,
              sortable: false,
              headerClass: 'justify-center',
              cellClass: 'flex items-center justify-center',
              cellRenderer: (params: any) => {
                const transaction = params.data as WalletTransaction;
                const postToAccounting = Boolean(transaction.post_to_accounting);
                const entrySource = {
                  accounting_entry: transaction.accounting_entry ?? null,
                  accounting_entry_json: transaction.accounting_entry_json ?? null,
                };
                const reconciled = hasAccountingEntryPayload(entrySource);

                /** Misma caja 32×32 en los tres estados para alinear iconos en la celda. */
                const iconCellClass = 'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md';

                if (!postToAccounting) {
                  return (
                    <span
                      className={iconCellClass}
                      title={t('table.accountingEntry.naTitle')}
                      aria-label={t('table.accountingEntry.naTitle')}
                    >
                      <FontAwesomeIcon
                        icon={faFileInvoice}
                        className="h-4 w-4 opacity-70"
                        style={{ color: 'var(--wrh-icons)' }}
                      />
                    </span>
                  );
                }

                const title = reconciled
                  ? t('table.accountingEntry.reconciledTitle')
                  : t('table.accountingEntry.pendingTitle');

                const iconColor = reconciled ? 'var(--wrh-bright-success)' : 'var(--wrh-bright-error)';

                return (
                  <button
                    type="button"
                    className={cn(
                      iconCellClass,
                      'border-0 bg-transparent p-0 m-0 cursor-pointer transition-colors',
                      'hover:bg-muted/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    )}
                    title={title}
                    aria-label={title}
                    data-testid="wallet-transaction-accounting-entry"
                    onClick={() => onOpenAccountingEntry?.(transaction)}
                  >
                    <FontAwesomeIcon icon={faFileInvoice} className="h-4 w-4" style={{ color: iconColor }} />
                  </button>
                );
              },
            } as CustomColDef,
          ]
        : []),
    ];

    return baseColumns;
  }, [currency, emptyValueLabel, isTenant3pl, isTenantExternal, onOpenAccountingEntry, t]);

  const filteredColumnDefs = useMemo(
    () => filterColumnsBySelection(allColumnDefs, selectedColumns),
    [allColumnDefs, selectedColumns],
  );

  const tanStackColumns = useMemo(() => convertToTanStackColumns(allColumnDefs), [allColumnDefs]);

  return {
    allColumnDefs,
    filteredColumnDefs,
    tanStackColumns,
  };
};
